// EditEventForm.jsx - Handles event EDITING only
import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import {
  FaAngleLeft,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import confirmDatePlugin from "flatpickr/dist/plugins/confirmDate/confirmDate";
import "flatpickr/dist/plugins/confirmDate/confirmDate.css";
import { uploadFile } from "../../firebase/fileUpload";
import EventAdditionalData from "../../components/eventAdditionalData/EventAdditionalData";
import SponsorDetailForm from "./SponsorDetailForm";
import SponsorParticipationTab from "./SponsorParticipationTab";
import MandatoryFollowsTab from "./MandatoryFollowsTab";
import IndependentMediaUpload from "./IndependentMediaUpload";
import EventFilesForm from "./EventFilesForm";
import EventReferralSettings from "./components/EventReferralSettings";
import OfflineEventSection from "./components/OfflineEventSection";
import { DataContext } from "../context/DataContext";
import { useEventValidation } from "./useEventValidation";
import { estimateCaptionBudget, MAX_CAPTION_LENGTH } from "../../helpers/captionBudget";
import ValidationReviewModal from "./ValidationReviewModal";

const EventType = Object.freeze({
  Short: "short",
  Long: "long",
});

const startDateOptions = { mode: "single", dateFormat: "Y-m-d" };
const endDateOptions = { mode: "single", dateFormat: "Y-m-d" };
const publishStartDateOptions = {
  mode: "single",
  dateFormat: "Y-m-d H:i",
  enableTime: true,
  time_24hr: false,
  altInput: true,
  altFormat: "Y-m-d h:i K",
  plugins: [
    new confirmDatePlugin({
      confirmIcon: "",
      confirmText: "DONE",
      showAlways: true,
      theme: "light"
    })
  ]
};
const publishEndDateOptions = {
  mode: "single",
  dateFormat: "Y-m-d H:i",
  enableTime: true,
  time_24hr: false,
  altInput: true,
  altFormat: "Y-m-d h:i K",
  plugins: [
    new confirmDatePlugin({
      confirmIcon: "",
      confirmText: "DONE",
      showAlways: true,
      theme: "light"
    })
  ]
};

const formatDateForInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  const month = `0${d.getMonth() + 1}`.slice(-2);
  const day = `0${d.getDate()}`.slice(-2);
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

const formatDateTimeForInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  const month = `0${d.getMonth() + 1}`.slice(-2);
  const day = `0${d.getDate()}`.slice(-2);
  const year = d.getFullYear();
  const hours = `0${d.getHours()}`.slice(-2);
  const mins = `0${d.getMinutes()}`.slice(-2);
  return `${year}-${month}-${day} ${hours}:${mins}`;
};

const parseStrToDate = (str) => {
  if (!str) return "";
  if (str instanceof Date) return str;
  const tMatch = String(str).match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/);
  if (tMatch) {
    return new Date(tMatch[1], tMatch[2] - 1, tMatch[3], tMatch[4], tMatch[5]);
  }
  const dMatch = String(str).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dMatch) {
    return new Date(dMatch[1], dMatch[2] - 1, dMatch[3]);
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

const findEventIdDeep = (obj) => {
  const KEYS = new Set(["eventID", "eventId", "event_id", "id"]);
  const seen = new Set();
  const walk = (n) => {
    if (!n || typeof n !== "object" || seen.has(n)) return null;
    seen.add(n);
    for (const k in n) {
      if (KEYS.has(k)) {
        const v = n[k];
        if (v !== undefined && v !== null && String(v).trim() !== "")
          return String(v);
      }
    }
    for (const k in n) {
      const r = walk(n[k]);
      if (r) return r;
    }
    return null;
  };
  return walk(obj);
};

const getParsedAdditionalData = (sourceObj) => {
  if (!sourceObj) return {};
  let data = sourceObj.additionalData || sourceObj.additional_data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (e) {
      data = {};
    }
  }
  return data || {};
};

const EditEventForm = () => {
  const apiDomain = process.env.REACT_APP_API_DOMAIN;
  const { upsertEvent, fetchEventById, singleEvent } = useContext(DataContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { eventId: urlEventId } = useParams();

  const [formData, setFormData] = useState({
    eventName: "",
    description: "",
    startDate: "",
    endDate: "",
    publishStartDate: "",
    publishEndDateTime: "",
    eventType: "",
    termsConditions: "",
    instructions: "",
    sponsorData: [],
    templateData: null,
    additionalData: {
      isPublish: false,
      eventCaption1: "",
      eventCaption2: "",
      eventHashtags: [],
    },
    referralSettings: {
      enableReferral: true,
      referralPercentageOverride: null,
      useCustomPercentage: false,
    },
    offlineConfig: {
      enabled: false,
      venue: { name: "", address: "", city: "", state: "", pincode: "", lat: 0, lng: 0, map_url: "" },
      days: [],
    },
  });

  const [eventId, setEventId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTemplateUploading, setIsTemplateUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showValidationWarnings, setShowValidationWarnings] = useState(false);

  const [mediaDraft, setMediaDraft] = useState({
    imageData: [],
    videoData: [],
    musicData: [],
    videoOutputDuration: 0,
    musicDuration: 0,
  });

  const mediaRef = useRef(null);
  const mediaTabVisited = useRef(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [sponsorSubTab, setSponsorSubTab] = useState("packages");
  const [hasInitialized, setHasInitialized] = useState(false); // Track initialization with state
  const isLong = (formData.eventType || "").toLowerCase() === EventType.Long;

  // Initialize validation hook
  const {
    getFieldValidation,
    getAllValidationIssues,
    getStepValidationIssues,
  } = useEventValidation(formData, mediaDraft, mediaRef, isLong);

  // Compute hasBlockingIssues for publish mode validation
  // Media validation is only enforced after the Competition Reels tab has been
  // visited at least once (the component fetches existing data on mount, so
  // mediaDraft is empty until then — we must not penalise a fresh page-load).
  const allIssues = getAllValidationIssues().filter((i) => {
    if (i.field === 'mediaRequired' && !mediaTabVisited.current) return false;
    return true;
  });
  const hasBlockingIssues = allIssues.filter(i => i.severity === 'critical').length > 0;

  const stepLabels = [
    "Event Details",
    "Additional Details",
    "Sponsors",
    ...(isLong ? ["Competition Reels"] : []),
    "Required Files",
    "Review & Submit",
  ];
  const totalSteps = stepLabels.length;

  // Load event data from navigation state or fetch from API
  useEffect(() => {
    // Only initialize once
    if (hasInitialized) {
      return;
    }

    const event = location?.state?.event;

    if (event) {
      setHasInitialized(true);
      setIsLoading(false);
      const resolvedEventId = event.eventID || event.eventId || event.id || null;
      setEventId(resolvedEventId);

      // Extract additionalData and ensure isPublish is properly read
      const eventAdditionalData = getParsedAdditionalData(event);
      const isPublishValue = eventAdditionalData.isPublish === true || eventAdditionalData.isPublish === 'true' || eventAdditionalData.isPublish === 1;

      console.log('=== Loading event, isPublish:', isPublishValue, 'PublishDates:', eventAdditionalData.publishStartDate, eventAdditionalData.publishEndDateTime);

      setFormData({
        eventName: event.eventName || '',
        description: event.description || '',
        startDate: event.startDate || '',
        endDate: event.endDate || '',
        publishStartDate:
          event.publishStartDate ||
          eventAdditionalData.publishStartDate ||
          '',
        publishEndDateTime:
          event.publishEndDateTime ||
          eventAdditionalData.publishEndDateTime ||
          '',
        eventType: event.eventType || '',
        termsConditions: event.termsConditions || '',
        instructions: event.instructions || '',
        sponsorData: event.sponsorData || [],
        templateData: event.templateData || null,
        additionalData: {
          ...eventAdditionalData,
          isPublish: isPublishValue,
        },
      });
      if (event.templateData) setImagePreview(event.templateData);

      // Always fetch full event data from API to supplement fields that may be
      // missing from the list view (e.g. publishStartDate is not returned by
      // getfilteredevent but IS returned by getevent).
      if (resolvedEventId) {
        fetchEventById(resolvedEventId);
      }
    } else if (urlEventId && !hasInitialized) {
      // Need to fetch from API
      setEventId(urlEventId);
      fetchEventById(urlEventId);
    }
  }, [location?.state, urlEventId, fetchEventById, hasInitialized]);

  // When singleEvent is fetched, populate or update form data.
  // This ensures that even if we initialised from stale navigation state,
  // the fresh data from the server will ultimately prevail.
  useEffect(() => {
    if (!singleEvent) return;

    // Use singleEvent to populate/update the form.
    // NOTE: We only automatically overwrite fields that haven't been 'touched' 
    // yet, or simply all of them if this is the first time server data arrived.
    const eventAdditionalData = getParsedAdditionalData(singleEvent);
    const isPublishValue = eventAdditionalData.isPublish === true || eventAdditionalData.isPublish === 'true' || eventAdditionalData.isPublish === 1;

    setFormData((prev) => {
      // If we are already initialized and have different data, we should prefer server truth
      // unless we want to avoid overwriting user edits. In this specific app,
      // it's better to ensure freshness on load.
      return {
        ...prev,
        eventName: singleEvent.eventName || prev.eventName || '',
        description: singleEvent.description || prev.description || '',
        startDate: singleEvent.startDate || prev.startDate || '',
        endDate: singleEvent.endDate || prev.endDate || '',
        publishStartDate:
          singleEvent.publishStartDate ||
          eventAdditionalData.publishStartDate ||
          prev.publishStartDate ||
          '',
        publishEndDateTime:
          singleEvent.publishEndDateTime ||
          eventAdditionalData.publishEndDateTime ||
          prev.publishEndDateTime ||
          '',
        eventType: singleEvent.eventType || prev.eventType || '',
        termsConditions: singleEvent.termsConditions || prev.termsConditions || '',
        instructions: singleEvent.instructions || prev.instructions || '',
        sponsorData: (singleEvent.sponsorData && singleEvent.sponsorData.length > 0) ? singleEvent.sponsorData : prev.sponsorData,
        templateData: singleEvent.templateData || prev.templateData,
        additionalData: {
          ...prev.additionalData,
          ...eventAdditionalData,
          isPublish: isPublishValue,
          // Specifically merge nested participationPost accurately
          participationPost: {
            ...(prev.additionalData?.participationPost || {}),
            ...(eventAdditionalData.participationPost || {}),
          }
        },
        referralSettings: singleEvent.referralSettings || prev.referralSettings || {
          enableReferral: true,
          referralPercentageOverride: null,
          useCustomPercentage: false,
        },
      };
    });

    if (singleEvent.templateData) {
      setImagePreview(singleEvent.templateData);
    }

    setHasInitialized(true);
    setIsLoading(false);
  }, [singleEvent]);

  // Fetch offline_config (venue + days) when entering the edit form. Stored
  // separately under /events/<eid>/offline_config so /api/v2/getevent doesn't
  // hydrate it — we pull it ourselves so the offline section pre-fills.
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${apiDomain}/api/v2/admin/events/offline-config`,
          { params: { event_id: eventId, t: Date.now() } },
        );
        if (cancelled) return;
        const cfg = res?.data || {};
        setFormData((prev) => ({
          ...prev,
          offlineConfig: {
            enabled: !!cfg.enabled,
            venue: { ...(prev.offlineConfig?.venue || {}), ...(cfg.venue || {}) },
            days: Array.isArray(cfg.days) ? cfg.days : [],
          },
        }));
      } catch (err) {
        // Missing config is the normal case for online-only events — silent.
        if (err?.response?.status && err.response.status !== 404) {
          console.error("offline-config load failed:", err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [apiDomain, eventId]);

  // Fetch long event media data to avoid data loss if tab is not visited
  useEffect(() => {
    const fetchMediaData = async () => {
      if (!eventId || !isLong) return;
      try {
        const response = await axios.get(`${apiDomain}/api/v2/getlongevent`, {
          params: { event_id: eventId, t: Date.now() }, // Cache-busting
        });
        if (response.status === 200 && response.data) {
          const data = response.data;
          setMediaDraft({
            imageData: JSON.parse(data.image_data || "[]"),
            videoData: JSON.parse(data.video_data || "[]"),
            musicData: JSON.parse(data.music_data || "[]"),
            videoOutputDuration: data.video_output_duration_in_secs || 0,
            musicDuration: data.music_duration_in_secs || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching media data in EditEventForm:", error);
      }
    };

    if (eventId && isLong) {
      fetchMediaData();
    }
  }, [apiDomain, eventId, isLong]);



  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      additionalData: {
        ...(prev.additionalData || {}),
        isPublish: checked,
      },
    }));
  };

  const handleTextareaResize = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  // Filtered issues: suppress mediaRequired until Competition Reels tab has been visited
  const getFilteredStepIssues = (stepIndex) =>
    getStepValidationIssues(stepIndex).filter((i) => {
      if (i.field === 'mediaRequired' && !mediaTabVisited.current) return false;
      return true;
    });

  // Hard validation - used for publish mode, blocks progression
  const validateStep1_EventBasics = () => {
    const issues = getStepValidationIssues(1);
    if (issues.length > 0) {
      setCurrentStep(1);
      setShowValidationWarnings(true);
      setTimeout(() => {
        const errorEl = document.querySelector(`.error-field-${issues[0].field}`) || document.querySelector('.error-field');
        if (errorEl) errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return false;
    }
    return true;
  };

  const validateStep2_Additional = () => {
    const issues = getStepValidationIssues(2);
    if (issues.length > 0) {
      setCurrentStep(2);
      setShowValidationWarnings(true);
      return false;
    }
    return true;
  };

  const validateStep3_Sponsors = () => {
    const issues = getStepValidationIssues(3);
    if (issues.length > 0) {
      setCurrentStep(3);
      setShowValidationWarnings(true);
      return false;
    }
    return true;
  };

  const validateStep4_Template = () => {
    if (isTemplateUploading) {
      setShowValidationWarnings(true);
      return false;
    }
    const issues = getFilteredStepIssues(4);
    if (issues.length > 0) {
      setCurrentStep(4);
      setShowValidationWarnings(true);
      return false;
    }
    return true;
  };

  const validateStep5_MediaIfLong = () => {
    if (!isLong) return true;
    const issues = getStepValidationIssues(5);
    if (issues.length > 0) {
      setCurrentStep(5);
      setShowValidationWarnings(true);
      return false;
    }
    return true;
  };

  // Allow skipping with confirmation if there are issues
  const nextStep = (forceSkip = false) => {
    const stepIssues = getStepValidationIssues(currentStep);

    if (stepIssues.length > 0 && !forceSkip) {
      setShowValidationWarnings(true);

      const firstIssue = stepIssues[0];
      const fieldName = firstIssue.field;

      setTimeout(() => {
        const errorEl = document.querySelector(`.error-field-${fieldName}`) || document.querySelector('.error-field');
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      return;
    }

    setShowValidationWarnings(false);
    setCurrentStep((s) => Math.min(s + 1, totalSteps));
  };

  const prevStep = () => setCurrentStep((s) => Math.max(s - 1, 1));

  // Allow direct navigation without blocking
  const goToStep = (n) => {
    setCurrentStep(n);
  };

  // Actual submission after review
  const handleConfirmedSubmit = async () => {
    setShowReviewModal(false);
    await handleEditSubmit();
  };

  const handleTemplateUpload = async (file) => {
    try {
      if (!file) return null;
      setImagePreview(URL.createObjectURL(file));
      setIsTemplateUploading(true);
      const uploadedUrl = await uploadFile(file);
      setFormData((prev) => ({ ...prev, templateData: uploadedUrl }));
      return uploadedUrl;
    } catch (error) {
      console.error("Template upload failed: - EditEventForm.js:301", error);
      alert("Error uploading template file. Please try again.");
      return null;
    } finally {
      setIsTemplateUploading(false);
    }
  };

  const isBlobUrl = (s) => typeof s === "string" && s.startsWith("blob:");

  const toInt = (v) => {
    const x = parseInt(String(v ?? 0), 10);
    return Number.isFinite(x) ? x : 0;
  };

  const toPositiveInt = (v) => {
    const x = parseInt(String(v ?? ""), 10);
    return Number.isFinite(x) && x > 0 ? x : null;
  };

  const sanitizeMediaList = (arr = []) =>
    (Array.isArray(arr) ? arr : [])
      .map((it) => {
        if (!it) return null;
        const label = String(it.label ?? "").trim();
        const url = String(it.url ?? "").trim();
        if (!url || isBlobUrl(url)) return null;
        return {
          label,
          url,
          description: String(it.description ?? ""),
          duration: toInt(it.duration),
          mandatory: !!it.mandatory,
          default: !!it.default,
          dontMute: !!it.dontMute,
        };
      })
      .filter(Boolean);

  const buildMediaPayload = (refInstance, draft) => {
    if (refInstance && typeof refInstance.getPayload === "function") {
      const p = refInstance.getPayload();
      const images = (() => {
        try {
          return JSON.parse(p.image_data || "[]");
        } catch {
          return [];
        }
      })();
      const videos = (() => {
        try {
          return JSON.parse(p.video_data || "[]");
        } catch {
          return [];
        }
      })();
      const music = (() => {
        try {
          return JSON.parse(p.music_data || "[]");
        } catch {
          return [];
        }
      })();
      const imgSan = sanitizeMediaList(images);
      const vidSan = sanitizeMediaList(videos);
      const musSan = sanitizeMediaList(music);
      return {
        number_of_images: imgSan.length,
        number_of_videos: vidSan.length,
        number_of_music: musSan.length,
        image_data: JSON.stringify(imgSan),
        video_data: JSON.stringify(vidSan),
        music_data: JSON.stringify(musSan),
        video_output_duration_in_secs: toInt(
          p.video_output_duration_in_secs ?? p.videoOutputDuration
        ),
        music_duration_in_secs: toInt(
          p.music_duration_in_secs ?? p.musicDuration
        ),
      };
    }

    const imgSan = sanitizeMediaList(draft.imageData || []);
    const vidSan = sanitizeMediaList(draft.videoData || []);
    const musSan = sanitizeMediaList(draft.musicData || []);
    return {
      number_of_images: imgSan.length,
      number_of_videos: vidSan.length,
      number_of_music: musSan.length,
      image_data: JSON.stringify(imgSan),
      video_data: JSON.stringify(vidSan),
      music_data: JSON.stringify(musSan),
      video_output_duration_in_secs: toInt(draft.videoOutputDuration),
      music_duration_in_secs: toInt(draft.musicDuration),
    };
  };

  const handleEditSubmit = async (e) => {
    e?.preventDefault?.();
    if (isSubmitting) return;

    const isPublishMode = !!formData?.additionalData?.isPublish;

    try {
      setIsSubmitting(true);

      // For publish mode, all validations must pass
      if (isPublishMode) {
        if (!validateStep1_EventBasics()) return;
        if (!validateStep2_Additional()) return;
        if (!validateStep3_Sponsors()) return;
        if (!validateStep4_Template()) return;
        if (isLong && !validateStep5_MediaIfLong()) return;
      } else {
        // For draft mode, only check critical issues (template upload status)
        if (isTemplateUploading) {
          setShowValidationWarnings(true);
          return;
        }
        if (formData.templateData && String(formData.templateData).startsWith("blob:")) {
          setShowValidationWarnings(true);
          return;
        }
      }

      // Check for authentication token - prioritize fbauthToken (real FB token) over authToken (short admin token)
      const accessToken = Cookies.get("fbauthToken") || Cookies.get("authToken");
      if (!accessToken) {
        alert("Not authenticated. Please log in.");
        return;
      }

      const eventPayload = {
        eventName: formData.eventName,
        description: formData.description,
        eventType: formData.eventType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        termsConditions: formData.termsConditions,
        instructions: formData.instructions,
        templateData: formData.templateData,
        additionalData: {
          ...formData.additionalData,
          publishStartDate: formData.publishStartDate,
          publishEndDateTime: formData.publishEndDateTime,
        },
        sponsorData: Array.isArray(formData.sponsorData) ? formData.sponsorData : [],
        referralSettings: formData.referralSettings,
      };

      const eventResp = await axios.put(
        `${apiDomain}/api/v2/updateevent?id=${eventId}&access_token=${accessToken}`,
        eventPayload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (![200, 201].includes(eventResp?.status)) {
        throw new Error(`Event update failed (${eventResp?.status})`);
      }

      const updatedEvent = eventResp.data;
      const updatedEventId = findEventIdDeep(updatedEvent) || eventId;

      // Construct a complete event object for local state update. 
      // The API response (updatedEvent) typically only contains a success message,
      // so we MUST use formData as the base to avoid data loss in the UI.
      const normalizedEvent = {
        ...formData,
        eventID: updatedEventId,
        // preserve publish dates which are part of additionalData as well
        publishStartDate: formData.publishStartDate,
        publishEndDateTime: formData.publishEndDateTime,
        additionalData: {
          ...formData.additionalData,
          publishStartDate: formData.publishStartDate,
          publishEndDateTime: formData.publishEndDateTime,
        },
      };


      // Skip separate sponsors update - already included in main event payload above

      // Update media for long events via longevent endpoint
      if (isLong) {
        const mediaPayload = buildMediaPayload(mediaRef.current, mediaDraft);
        const updatedEventIdNum = toPositiveInt(updatedEventId);
        if (!updatedEventIdNum) {
          throw new Error("Invalid event id for media update");
        }
        // Add event_id to payload - backend requires it in the body, not just in query params
        const mediaPayloadWithId = {
          ...mediaPayload,
          event_id: updatedEventIdNum,
        };

        console.log('=== Updating media for long event ===');
        console.log('Event ID:', updatedEventIdNum);
        console.log('Media payload:', JSON.stringify(mediaPayloadWithId, null, 2));
        console.log('mediaRef.current:', mediaRef.current);
        console.log('mediaDraft:', mediaDraft);

        const mediaResp = await axios.post(
          `${apiDomain}/api/v2/longevent/createorupdate?access_token=${accessToken}`,
          mediaPayloadWithId,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        console.log('Media update response:', mediaResp.status, mediaResp.data);

        if (![200, 201].includes(mediaResp?.status)) {
          throw new Error(`Media update failed (${mediaResp?.status})`);
        }
      }

      // Persist offline-event config alongside the event update. Always send
      // — even when disabled — so flipping the toggle off on a previously
      // offline event clears it cleanly.
      try {
        await axios.put(
          `${apiDomain}/api/v2/admin/events/offline-config?event_id=${updatedEventId}&access_token=${accessToken}`,
          {
            enabled: !!formData.offlineConfig?.enabled,
            venue: formData.offlineConfig?.venue || {},
            days: Array.isArray(formData.offlineConfig?.days) ? formData.offlineConfig.days : [],
          },
          { headers: { "Content-Type": "application/json" } },
        );
      } catch (cfgErr) {
        console.error("offline-config save failed:", cfgErr);
        alert("Event updated, but saving offline check-in config failed.");
      }

      alert("Event updated successfully!");
      upsertEvent?.(normalizedEvent);
      navigate("/events", { replace: true, state: { updatedEvent: normalizedEvent } });
    } catch (error) {
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        (typeof error?.response?.data === "string" ? error?.response?.data : null);
      const displayMsg = serverMsg || error?.message || "An error occurred";
      alert(`Failed to update event: ${displayMsg}`);
      console.error("Event update failed: - EditEventForm.js:499", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to get validation highlight class for a field
  const getValidationClass = (fieldName, baseClass = "") => {
    if (!showValidationWarnings) return baseClass;
    const validation = getFieldValidation(fieldName);
    if (!validation.isValid && validation.required) {
      return `${baseClass} border-red-500 ring-1 ring-red-500 bg-red-50 error-field-${fieldName}`;
    }
    return baseClass;
  };

  // Render validation warning banner for current step
  const renderStepValidationWarning = () => {
    const stepIssues = getStepValidationIssues(currentStep);
    if (stepIssues.length === 0) return null;

    return (
      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg flex items-start gap-3">
        <FaExclamationTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-yellow-800">
            {stepIssues.length} field{stepIssues.length > 1 ? "s" : ""} need{stepIssues.length === 1 ? "s" : ""} attention
          </p>
          <p className="text-sm text-yellow-700">
            Complete these fields before publishing, or continue to save as draft.
          </p>
        </div>
        <button
          type="button"
          onClick={() => nextStep(true)}
          className="text-yellow-700 hover:text-yellow-900 text-sm underline"
        >
          Skip for now
        </button>
      </div>
    );
  };

  const renderStepEventDetails = () => (
    <div className="w-full space-y-6">
      <h3 className="text-xl font-semibold text-blue-600">Event Details</h3>
      {showValidationWarnings && renderStepValidationWarning()}

      {/* ── Group 1: Basic Information ── */}
      <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
        <h4 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
          Basic Information
        </h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              Event Type <span className="text-red-500">*</span>
            </span>
            <select
              name="eventType"
              value={formData.eventType}
              onChange={handleChange}
              className={getValidationClass("eventType", "w-full h-12 border rounded px-4")}
            >
              <option value="">Select Event Type</option>
              <option value="short">Short</option>
              <option value="long">Long</option>
            </select>
            {showValidationWarnings && !getFieldValidation("eventType").isValid && (
              <span className="text-red-600 text-sm">⚠ Event type is required</span>
            )}
          </div>

          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              Event Name <span className="text-red-500">*</span>
            </span>
            <input
              type="text"
              name="eventName"
              value={formData.eventName}
              onChange={handleChange}
              placeholder="Enter Event Name"
              className={getValidationClass("eventName", "w-full h-12 border rounded px-4")}
            />
            {showValidationWarnings && !getFieldValidation("eventName").isValid && (
              <span className="text-red-600 text-sm">⚠ Event name is required</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Pricing ── */}
      <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
        <h4 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
          Pricing
        </h4>
        <div className="flex flex-col items-start space-y-2">
          <span className="starlabel font-medium text-lg">Event Price</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={
              formData.additionalData?.eventPrice === null ||
                formData.additionalData?.eventPrice === undefined
                ? ""
                : formData.additionalData.eventPrice
            }
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9]/g, "");
              setFormData((prev) => ({
                ...prev,
                additionalData: {
                  ...(prev.additionalData || {}),
                  eventPrice: v === "" ? null : Number(v),
                },
              }));
            }}
            onKeyDown={(e) => {
              if (
                !/^\d$/.test(e.key) &&
                !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End"].includes(e.key) &&
                !e.ctrlKey &&
                !e.metaKey
              )
                e.preventDefault();
            }}
            onFocus={(e) => e.target.select()}
            className="w-full h-12 border rounded px-4"
            placeholder="Enter price (0 = free)"
          />
        </div>
      </div>

      {/* ── Group 2: Event Schedule ── */}
      <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
        <h4 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
          Event Schedule
        </h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              Start Date <span className="text-red-500">*</span>
            </span>
            <Flatpickr
              value={parseStrToDate(formData.startDate)}
              onChange={(dates) => {
                setFormData((prev) => ({
                  ...prev,
                  startDate: dates[0] ? formatDateForInput(dates[0]) : "",
                }));
              }}
              options={startDateOptions}
              className={getValidationClass("startDate", "w-full h-12 border rounded px-4")}
              placeholder="Select Start Date"
            />
            {showValidationWarnings && !getFieldValidation("startDate").isValid && (
              <span className="text-red-600 text-sm">⚠ Start date is required</span>
            )}
          </div>

          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              End Date <span className="text-red-500">*</span>
            </span>
            <Flatpickr
              value={parseStrToDate(formData.endDate)}
              onChange={(dates) => {
                setFormData((prev) => ({
                  ...prev,
                  endDate: dates[0] ? formatDateForInput(dates[0]) : "",
                }));
              }}
              options={endDateOptions}
              className={getValidationClass("endDate", "w-full h-12 border rounded px-4")}
              placeholder="Select End Date"
            />
            {showValidationWarnings && !getFieldValidation("endDate").isValid && (
              <span className="text-red-600 text-sm">⚠ End date is required</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Group 3: Publishing Schedule ── */}
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h4 className="text-base font-semibold text-gray-700 mb-1 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-gray-400 inline-block" />
          Publishing Schedule
          <span className="text-gray-400 text-sm font-normal">(optional – for post/reels)</span>
        </h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              Publish/Voting Start Date & Time
            </span>
            <Flatpickr
              value={parseStrToDate(formData.publishStartDate)}
              onChange={(dates) => {
                setFormData((prev) => ({
                  ...prev,
                  publishStartDate: dates[0] ? formatDateTimeForInput(dates[0]) : "",
                }));
              }}
              options={publishStartDateOptions}
              className="w-full h-12 border rounded px-4"
              placeholder="Select Publish Start Date & Time"
            />
          </div>

          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              Publish/Voting End Date & Time
            </span>
            <Flatpickr
              value={parseStrToDate(formData.publishEndDateTime)}
              onChange={(dates) => {
                setFormData((prev) => ({
                  ...prev,
                  publishEndDateTime: dates[0] ? formatDateTimeForInput(dates[0]) : "",
                }));
              }}
              options={publishEndDateOptions}
              className="w-full h-12 border rounded px-4"
              placeholder="Select Publish End Date & Time"
            />
          </div>
        </div>
      </div>

      {/* ── Group 4: Event Content ── */}
      <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
        <h4 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
          Event Content
        </h4>
        <div className="space-y-6">
          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              Event Description <span className="text-red-500">*</span>
            </span>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              onInput={handleTextareaResize}
              placeholder="Enter Event Description"
              className={getValidationClass("description", "w-full lg:min-h-40 border rounded px-4 py-2")}
            />
            {showValidationWarnings && !getFieldValidation("description").isValid && (
              <span className="text-red-600 text-sm">⚠ Description is required</span>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="flex flex-col items-start space-y-2">
              <span className="starlabel font-medium text-lg">
                Terms & Conditions <span className="text-red-500">*</span>
              </span>
              <textarea
                name="termsConditions"
                value={formData.termsConditions}
                onChange={handleChange}
                onInput={handleTextareaResize}
                placeholder="Enter Terms & Conditions"
                className={getValidationClass("termsConditions", "w-full lg:min-h-40 border rounded px-4 py-2")}
              />
              {showValidationWarnings && !getFieldValidation("termsConditions").isValid && (
                <span className="text-red-600 text-sm">⚠ Terms & Conditions are required</span>
              )}
            </div>

            <div className="flex flex-col items-start space-y-2">
              <span className="starlabel font-medium text-lg">
                Instructions <span className="text-red-500">*</span>
              </span>
              <textarea
                name="instructions"
                value={formData.instructions}
                onChange={handleChange}
                onInput={handleTextareaResize}
                placeholder="Enter Instructions"
                className={getValidationClass("instructions", "w-full lg:min-h-40 border rounded px-4 py-2")}
              />
              {showValidationWarnings && !getFieldValidation("instructions").isValid && (
                <span className="text-red-600 text-sm">⚠ Instructions are required</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Group 5: Referral Settings ── */}
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <EventReferralSettings
          referralSettings={formData.referralSettings}
          onChange={(settings) =>
            setFormData((prev) => ({ ...prev, referralSettings: settings }))
          }
        />
      </div>

      {/* ── Group 6: Offline Event Check-In ── */}
      <OfflineEventSection
        value={formData.offlineConfig}
        onChange={(cfg) =>
          setFormData((prev) => ({ ...prev, offlineConfig: cfg }))
        }
      />

    </div>
  );

  const renderStepAdditional = () => (
    <EventAdditionalData
      additionalData={formData.additionalData}
      setAdditionalData={(data) =>
        setFormData((prev) => ({
          ...prev,
          additionalData: {
            ...data,
            // IMPORTANT: Preserve fields NOT managed by EventAdditionalData
            isPublish: prev.additionalData?.isPublish ?? false,
            sponsorParticipationPost: prev.additionalData?.sponsorParticipationPost,
            // Competition Reel frame lives in the Competition Reels tab
            competitionReelFrameImage: prev.additionalData?.competitionReelFrameImage,
            // Required Files (event-level) — managed in Required Files tab
            requiredFiles: prev.additionalData?.requiredFiles,
            requiredFilesEnabled: prev.additionalData?.requiredFilesEnabled,
            // Sponsor Required Files — managed in Sponsors > Sponsor Required Files tab
            sponsorRequiredFiles: prev.additionalData?.sponsorRequiredFiles,
            sponsorRequiredFilesEnabled: prev.additionalData?.sponsorRequiredFilesEnabled,
            // Post Carousel — managed in Competition Reels tab
            isPostCarousel: prev.additionalData?.isPostCarousel,
            postCarouselConfig: prev.additionalData?.postCarouselConfig,
            // Sponsor Terms & Conditions — global, managed in Sponsors tab
            sponsorTermsConditions: prev.additionalData?.sponsorTermsConditions,
            // Sponsor Participation Carousel — managed in Sponsors > Participation tab
            isSponsorParticipationCarousel: prev.additionalData?.isSponsorParticipationCarousel,
            sponsorParticipationCarouselConfig: prev.additionalData?.sponsorParticipationCarouselConfig,
          },
        }))
      }
      isEditMode={true}
      onFinish={() => setCurrentStep(3)}
      templateData={formData.templateData}
      onTemplateDataChange={(url) =>
        setFormData((prev) => ({ ...prev, templateData: url }))
      }
    />
  );

  const renderStepSponsors = () => (
    <div>
      {/* Sub-tab bar */}
      <div className="flex border-b border-gray-200 mb-6">
        {[
          { id: "packages", label: "📦 Sponsor Packages" },
          { id: "participation", label: "📸 Participation Post" },
          { id: "mandatory", label: "🤝 Mandatory Follows" },
          { id: "sponsorFiles", label: "📄 Sponsor Required Files" },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setSponsorSubTab(tab.id)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${sponsorSubTab === tab.id
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {sponsorSubTab === "packages" && (
        <SponsorDetailForm
          savedCards={formData.sponsorData}
          sponsorTermsConditions={formData.additionalData?.sponsorTermsConditions || ""}
          onTermsConditionsChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: {
                ...(prev.additionalData || {}),
                sponsorTermsConditions: val,
              },
            }))
          }
          onSponsorDataChange={(data) =>
            setFormData((prev) => ({
              ...prev,
              sponsorData: [...(prev.sponsorData || []), data],
            }))
          }
          onDeleteSponsor={(index) =>
            setFormData((prev) => ({
              ...prev,
              sponsorData: (prev.sponsorData || []).filter((_, i) => i !== index),
            }))
          }
          onUpdateSponsor={(index, data) =>
            setFormData((prev) => ({
              ...prev,
              sponsorData: (prev.sponsorData || []).map((item, i) =>
                i === index ? data : item
              ),
            }))
          }
        />
      )}

      {sponsorSubTab === "participation" && (
        <SponsorParticipationTab
          ppData={formData.additionalData?.sponsorParticipationPost || {}}
          onDataChange={(updatedPP) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: {
                ...(prev.additionalData || {}),
                sponsorParticipationPost: updatedPP,
              },
            }))
          }
          isSponsorParticipationCarousel={
            formData.additionalData?.isSponsorParticipationCarousel || false
          }
          setIsSponsorParticipationCarousel={(val) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: {
                ...(prev.additionalData || {}),
                isSponsorParticipationCarousel: val,
              },
            }))
          }
          sponsorParticipationCarouselConfig={
            formData.additionalData?.sponsorParticipationCarouselConfig || {
              adminMediaCount: 0,
              userMediaCount: 0,
              mediaItems: [],
            }
          }
          setParticipationCarouselConfig={(val) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: {
                ...(prev.additionalData || {}),
                sponsorParticipationCarouselConfig: val,
              },
            }))
          }
        />
      )}

      {sponsorSubTab === "mandatory" && (
        <MandatoryFollowsTab
          initialAccounts={formData.additionalData?.mandatoryFollows || []}
          onDataChange={(updatedAccounts) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: {
                ...(prev.additionalData || {}),
                mandatoryFollows: updatedAccounts,
              },
            }))
          }
        />
      )}


      {sponsorSubTab === "sponsorFiles" && (
        <EventFilesForm
          files={formData.additionalData?.sponsorRequiredFiles || []}
          setFiles={(files) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: { ...prev.additionalData, sponsorRequiredFiles: files },
            }))
          }
          enabled={!!formData.additionalData?.sponsorRequiredFilesEnabled}
          setEnabled={(val) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: { ...prev.additionalData, sponsorRequiredFilesEnabled: val },
            }))
          }
        />
      )}
    </div>
  );

  const renderStepTemplate = () => (
    <div className="w-full p-4 border rounded-md mt-4 bg-blue-50 shadow-md">
      <h3 className="text-xl lg:text-2xl font-semibold text-gray-700">
        Template Frame Image
      </h3>
      <div className="mt-2 flex items-center gap-3">
        <input
          type="file"
          accept="image/*"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file) await handleTemplateUpload(file);
          }}
          className="w-fit bg-white rounded-lg p-4"
        />
        {isTemplateUploading && (
          <span className="inline-flex items-center text-sm text-blue-700">
            <FaSpinner className="mr-2 animate-spin" /> Uploading template…
          </span>
        )}
      </div>
      {imagePreview && (
        <img
          src={imagePreview}
          alt="Template Preview"
          className="mt-4 w-60 h-60 lg:w-96 lg:h-96 object-cover rounded"
        />
      )}
      {!formData.templateData && !isTemplateUploading && (
        <div className={`mt-2 p-2 rounded ${getValidationClass("templateData", "")}`}>
          <p className="text-sm text-gray-600">
            ⚠ Please upload a template frame image to continue.
          </p>
        </div>
      )}
    </div>
  );

  const renderStepFiles = () => (
    <EventFilesForm
      files={formData.additionalData?.requiredFiles || []}
      setFiles={(files) =>
        setFormData((prev) => ({
          ...prev,
          additionalData: { ...prev.additionalData, requiredFiles: files },
        }))
      }
      enabled={!!formData.additionalData?.requiredFilesEnabled}
      setEnabled={(val) =>
        setFormData((prev) => ({
          ...prev,
          additionalData: { ...prev.additionalData, requiredFilesEnabled: val },
        }))
      }
    />
  );

  const renderStepMedia = () => {
    mediaTabVisited.current = true;
    return (
      <div className="mt-4">
        {isLong && showValidationWarnings && getFilteredStepIssues(4).length > 0 && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg flex items-start gap-3">
            <FaExclamationTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-yellow-800">Media required for long events</p>
              <p className="text-sm text-yellow-700">
                Add at least one image, video, or music track.
              </p>
            </div>
          </div>
        )}
        <IndependentMediaUpload
          ref={mediaRef}
          eventId={eventId}
          draft={mediaDraft}
          onDraftChange={setMediaDraft}
          ppCaption2={formData?.additionalData?.participationPost?.caption2 || ""}
          onPpCaption2Change={(val) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: {
                ...(prev.additionalData || {}),
                participationPost: {
                  ...(prev.additionalData?.participationPost || {}),
                  caption2: val,
                },
              },
            }))
          }
          isPostCarousel={formData?.additionalData?.isPostCarousel || false}
          setIsPostCarousel={(val) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: {
                ...(prev.additionalData || {}),
                isPostCarousel: val
              }
            }))
          }
          postCarouselConfig={formData?.additionalData?.postCarouselConfig || { adminMediaCount: 0, userMediaCount: 0, mediaItems: [] }}
          setPostCarouselConfig={(val) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: {
                ...(prev.additionalData || {}),
                postCarouselConfig: val
              }
            }))
          }
          frameImageUrl={formData?.additionalData?.competitionReelFrameImage || null}
          onFrameImageUrlChange={(url) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: {
                ...(prev.additionalData || {}),
                competitionReelFrameImage: url,
              },
            }))
          }
        />
      </div>
    );
  };

  const renderStepReview = () => {
    // Filter out media validation if the Competition Reels tab has never been
    // visited — the media component only hydrates when it mounts, so until then
    // an empty mediaDraft is a false-positive, not a real error.
    const allIssues = getAllValidationIssues().filter((i) => {
      if (i.field === 'mediaRequired' && !mediaTabVisited.current) return false;
      return true;
    });
    const isPublishMode = !!formData?.additionalData?.isPublish;
    const hasBlockingIssues = isPublishMode && allIssues.length > 0;

    return (
      <div className="w-full p-4 border rounded-md bg-blue-50">
        <h3 className="text-xl font-semibold text-blue-600 mb-4">
          Review & Submit
        </h3>

        {/* Validation Summary */}
        {allIssues.length > 0 && (
          <div className={`mb-4 p-4 rounded-lg border ${isPublishMode ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'}`}>
            <div className="flex items-start gap-3">
              <FaExclamationTriangle className={isPublishMode ? "text-red-500" : "text-yellow-500"} size={20} />
              <div className="flex-1">
                <p className={`font-semibold ${isPublishMode ? 'text-red-800' : 'text-yellow-800'}`}>
                  {allIssues.length} field{allIssues.length > 1 ? 's' : ''} need{allIssues.length === 1 ? 's' : ''} attention
                </p>
                <ul className="mt-2 text-sm space-y-1">
                  {allIssues.slice(0, 5).map((issue, idx) => (
                    <li key={idx} className={isPublishMode ? 'text-red-700' : 'text-yellow-700'}>
                      • Step {issue.step}: {issue.message}
                      <button
                        type="button"
                        onClick={() => goToStep(issue.step)}
                        className="ml-2 text-blue-600 hover:underline text-xs"
                      >
                        Fix it →
                      </button>
                    </li>
                  ))}
                  {allIssues.length > 5 && (
                    <li className="text-gray-600">...and {allIssues.length - 5} more</li>
                  )}
                </ul>
                {isPublishMode && (
                  <p className="mt-2 text-sm text-red-600 font-medium">
                    ⚠ You must complete all required fields to publish this event.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {allIssues.length === 0 && (
          <div className="mb-4 p-4 bg-green-50 border border-green-300 rounded-lg">
            <div className="flex items-center gap-3">
              <FaCheckCircle className="text-green-500" size={20} />
              <div>
                <p className="font-semibold text-green-800">All fields are complete!</p>
                <p className="text-sm text-green-700">Your event is ready to be {isPublishMode ? 'published' : 'updated'}.</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Basic Details</h4>
              <button
                type="button"
                onClick={() => goToStep(1)}
                className="text-blue-600 hover:underline text-sm"
              >
                Edit
              </button>
            </div>
            <ul className="text-sm text-gray-700 space-y-1">
              <li className={!getFieldValidation("eventType").isValid ? "text-yellow-700 bg-yellow-50 p-1 rounded" : ""}>
                <b>Type:</b> {formData.eventType || <span className="text-yellow-600">Not set ⚠</span>}
              </li>
              <li className={!getFieldValidation("eventName").isValid ? "text-yellow-700 bg-yellow-50 p-1 rounded" : ""}>
                <b>Name:</b> {formData.eventName || <span className="text-yellow-600">Not set ⚠</span>}
              </li>
              <li className={!getFieldValidation("description").isValid ? "text-yellow-700 bg-yellow-50 p-1 rounded" : ""}>
                <b>Description:</b> {formData.description ? (formData.description.length > 50 ? formData.description.substring(0, 50) + "..." : formData.description) : <span className="text-yellow-600">Not set ⚠</span>}
              </li>
              <li className={!getFieldValidation("startDate").isValid ? "text-yellow-700 bg-yellow-50 p-1 rounded" : ""}>
                <b>Start Date:</b> {formData.startDate || <span className="text-yellow-600">Not set ⚠</span>}
              </li>
              <li className={!getFieldValidation("endDate").isValid ? "text-yellow-700 bg-yellow-50 p-1 rounded" : ""}>
                <b>End Date:</b> {formData.endDate || <span className="text-yellow-600">Not set ⚠</span>}
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Additional Info</h4>
              <button
                type="button"
                onClick={() => goToStep(2)}
                className="text-blue-600 hover:underline text-sm"
              >
                Edit
              </button>
            </div>
            <ul className="text-sm text-gray-700 space-y-1">
              <li className={!getFieldValidation("eventCaption2").isValid ? "text-yellow-700 bg-yellow-50 p-1 rounded" : ""}>
                <b>Caption 2:</b> {formData.additionalData?.eventCaption2 || <span className="text-yellow-600">Not set ⚠</span>}
              </li>
            </ul>
          </div>

          {(() => {
            const budget = estimateCaptionBudget({
              eventCaption1: formData.additionalData?.participationPost?.caption1 || "",
              eventCaption2: formData.additionalData?.eventCaption2 || "",
              hashtags: formData.additionalData?.participationPost?.hashtags || [],
            });
            const over = budget.isOver;
            const critical = budget.isCritical;
            const box = over
              ? "border-red-400 bg-red-50"
              : critical
              ? "border-yellow-400 bg-yellow-50"
              : "border-green-300 bg-green-50";
            return (
              <div className={`bg-white rounded-lg border-2 ${box} p-4 lg:col-span-2`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">
                    Caption Length Budget · Instagram limit {MAX_CAPTION_LENGTH}
                  </h4>
                  <button
                    type="button"
                    onClick={() => goToStep(2)}
                    className="text-blue-600 hover:underline text-sm"
                  >
                    Edit captions
                  </button>
                </div>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li><b>Participation post caption:</b> {budget.cap1Len} chars</li>
                  <li><b>Hashtag block:</b> {budget.hashtagBlockLen} chars</li>
                  <li><b>Referral code block:</b> ~{budget.refBlockLen} chars (depends on participant)</li>
                  <li className="pt-1 border-t">
                    <b>Estimated total before participant text:</b>{" "}
                    <span className={over ? "text-red-700 font-bold" : critical ? "text-yellow-700 font-bold" : "text-green-700 font-bold"}>
                      {budget.adminOverhead}/{MAX_CAPTION_LENGTH}
                    </span>
                  </li>
                  <li>
                    <b>Budget left for participant's own text:</b>{" "}
                    <span className={budget.userBudgetRemaining < 200 ? "text-red-700 font-bold" : "text-gray-800"}>
                      {budget.userBudgetRemaining} chars
                    </span>
                  </li>
                </ul>
                {over && (
                  <p className="mt-2 text-sm text-red-700 font-medium">
                    ⚠ Combined caption already exceeds Instagram's {MAX_CAPTION_LENGTH}-char limit. Shorten the Participation post caption before publishing — participants will get a "caption too long" rejection from Meta.
                  </p>
                )}
                {!over && critical && (
                  <p className="mt-2 text-sm text-yellow-700">
                    ⚠ Very little room left for the participant's own text. Consider trimming the Participation post caption.
                  </p>
                )}
              </div>
            );
          })()}

          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Sponsors</h4>
              <button
                type="button"
                onClick={() => goToStep(3)}
                className="text-blue-600 hover:underline text-sm"
              >
                Edit
              </button>
            </div>
            <div className={!getFieldValidation("sponsorData").isValid ? "text-yellow-700 bg-yellow-50 p-2 rounded" : ""}>
              <b>Sponsors:</b> {Array.isArray(formData.sponsorData) && formData.sponsorData.length > 0
                ? `${formData.sponsorData.length} added`
                : <span className="text-yellow-600">None added ⚠</span>}
            </div>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Template</h4>
              <button
                type="button"
                onClick={() => goToStep(2)}
                className="text-blue-600 hover:underline text-sm"
              >
                Edit
              </button>
            </div>
            <div className={!getFieldValidation("templateData").isValid ? "text-yellow-700 bg-yellow-50 p-2 rounded" : ""}>
              <b>Template Image:</b> {formData.templateData
                ? <span className="text-green-600">✓ Uploaded</span>
                : <span className="text-yellow-600">Not uploaded ⚠</span>}
            </div>
            {formData.templateData && (
              <img
                src={formData.templateData}
                alt="Template"
                className="mt-2 w-20 h-20 object-cover rounded border"
              />
            )}
          </div>
        </div>

        <div className="w-full mt-6 p-4 bg-white rounded-lg border">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!formData?.additionalData?.isPublish}
              onChange={handleCheckboxChange}
              className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div>
              <span className="text-gray-700 font-medium">Publish Event</span>
              <p className="text-sm text-gray-500">
                {formData?.additionalData?.isPublish
                  ? "Event will be visible to users immediately after update."
                  : "Event will be saved as draft. You can publish later."}
              </p>
            </div>
          </label>
          {hasBlockingIssues && (
            <p className="mt-2 text-sm text-red-600">
              ⚠ Cannot publish: Complete all {allIssues.length} required field{allIssues.length > 1 ? 's' : ''} first.
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (currentStep === 1) return renderStepEventDetails();
    if (currentStep === 2) return renderStepAdditional();
    if (currentStep === 3) return renderStepSponsors();
    if (isLong) {
      if (currentStep === 4) return renderStepMedia();
      if (currentStep === 5) return renderStepFiles();
      if (currentStep === 6) return renderStepReview();
    } else {
      if (currentStep === 4) return renderStepFiles();
      if (currentStep === 5) return renderStepReview();
    }
    return renderStepReview();
  };

  const labelAt = (i) => stepLabels[i - 1] || "";

  const StepPill = ({ index, label }) => {
    const done = index < currentStep;
    const active = index === currentStep;
    const stepIssues = getFilteredStepIssues(index);
    const hasIssues = stepIssues.length > 0;

    return (
      <button
        type="button"
        onClick={() => goToStep(index)}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition ${active
          ? "bg-blue-600 text-white border border-blue-600"
          : done && !hasIssues
            ? "bg-green-100 text-green-700 border border-green-300"
            : done && hasIssues
              ? "bg-yellow-100 text-yellow-700 border border-yellow-300"
              : "bg-gray-50 text-gray-700 border-gray-300"
          }`}
      >
        {done && !hasIssues ? (
          <FaCheckCircle className="text-green-600" />
        ) : done && hasIssues ? (
          <FaExclamationTriangle className="text-yellow-600" />
        ) : (
          <span className="w-2 h-2 rounded-full bg-current inline-block" />
        )}
        <span className="font-medium">{label}</span>
        {hasIssues && (
          <span className="bg-yellow-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {stepIssues.length}
          </span>
        )}
      </button>
    );
  };

  // No early return here anymore, we render the overlay conditionally below


  return (
    <div className="relative flex flex-col items-start m-2 lg:mr-8 pb-24">
      {/* Global Secure Loading Backdrop/Overlay */}
      {(isLoading || isSubmitting) && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-gray-900/40 backdrop-blur-md transition-all duration-300">
          <div className="bg-white p-8 rounded-2xl shadow-2xl text-center border border-gray-100 transform scale-110">
            <div className="relative w-16 h-16 mx-auto mb-6">
              <div className="absolute inset-0 border-4 border-blue-100 rounded-full"></div>
              <FaSpinner className="absolute inset-0 animate-spin text-blue-600 w-16 h-16" />
            </div>
            <h3 className="text-xl font-extrabold text-gray-800">
              {isSubmitting ? "Updating Event..." : "Fetching Secure Data..."}
            </h3>
            <p className="text-gray-500 mt-2 font-medium">
              {isSubmitting
                ? "Your changes are being saved to the server."
                : "Synchronizing the latest event configuration."}
            </p>
            <div className="mt-6 flex justify-center gap-1">
              <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
              <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
              <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></span>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      <ValidationReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        validationResults={getAllValidationIssues()}
        formData={formData}
        setFormData={setFormData}
        isPublishMode={!!formData?.additionalData?.isPublish}
        onGoToStep={goToStep}
        onConfirm={handleConfirmedSubmit}
        isSubmitting={isSubmitting}
        isLong={isLong}
        mediaDraft={mediaDraft}
      />

      <div className="flex items-center justify-between w-full mb-8">
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-700 transition flex items-center justify-center"
          >
            <FaAngleLeft size={18} />
          </button>
          <button
            onClick={() => navigate("/events")}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium transition"
          >
            Events
          </button>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600 font-medium">Edit Event</span>
          {formData?.eventName?.trim() && (
            <>
              <span className="text-gray-400">/</span>
              <button
                onClick={() => goToStep(1)}
                className="text-blue-600 hover:text-blue-800 hover:underline font-semibold text-base truncate max-w-[300px] transition"
                title={`Go to Event Details — ${formData.eventName}`}
              >
                {formData.eventName}
              </button>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={handleEditSubmit}
          disabled={isSubmitting || hasBlockingIssues}
          title={hasBlockingIssues ? "Complete all required fields to update" : "Update event"}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium transition"
        >
          {isSubmitting && <FaSpinner className="animate-spin" />}
          {isSubmitting ? "Updating..." : "Update Event"}
        </button>
      </div>

      <div className="w-full mb-6">
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: totalSteps }).map((_, i) => {
            const idx = i + 1;
            return <StepPill key={idx} index={idx} label={labelAt(idx)} />;
          })}
        </div>
        <div className="w-full h-1 bg-gray-200 rounded mt-3">
          <div
            className="h-1 bg-blue-600 rounded transition-all"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      <form className="w-full bg-white p-4 lg:p-6 rounded-lg shadow-md">
        {renderCurrentStep()}
      </form>

      <div className="w-full flex items-center gap-3 justify-between mt-8 mb-20">
        <button
          type="button"
          onClick={prevStep}
          disabled={currentStep === 1}
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Previous
        </button>

        <div className="flex items-center gap-3">
          {currentStep < totalSteps && (
            <button
              type="button"
              onClick={nextStep}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Next
            </button>
          )}
          {currentStep === totalSteps && (
            <>
              {/* Review & Submit Button - Opens modal for validation check */}
              <button
                type="button"
                onClick={() => setShowReviewModal(true)}
                className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center gap-2"
              >
                <FaExclamationTriangle />
                Review & Submit
              </button>

              {/* Direct Submit - Only if not in publish mode or no blocking issues */}
              {(!formData?.additionalData?.isPublish || !hasBlockingIssues) && (
                <button
                  type="button"
                  onClick={handleEditSubmit}
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmitting && <FaSpinner className="animate-spin" />}
                  {isSubmitting ? "Updating..." : "Update Event"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditEventForm;
